import { Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_AdvertisementDetailV3, Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_NieuwbouwTypeDetailV3 } from '@woonstad/api-client-woonstad-aanbod';

interface IDataLayerObject {
  event?: string;
  page?: string;
  [key: string]: any;
}

interface ICustomWindow extends Window {
  dataLayer?: IDataLayerObject[];
}

declare let window: ICustomWindow;

export type GoogleTagManagerArgs = {
  id: string;
  server?: string;
  auth?: string;
  environment?: string;
}

export const getIFrameURL = ({
  id,
  server,
  environment,
  auth,
}: GoogleTagManagerArgs) => {
  const BASE_URL = `https://${server}/ns.html`;

  if (!id) {
    console.warn('next-google-tag-manager: It looks like you forgot to configure your Google Tag Manager. For more information please check https://github.com/XD2Sketch/next-google-tag-manager#usage');
  }

  const queryParams = new URLSearchParams({
    id,
  });

  if (auth) {
    queryParams.append('gtm_auth', auth);
  }

  if (environment) {
    queryParams.append('gtm_preview', `env-${environment}`);
    queryParams.append('gtm_cookies_win', 'x');
  }

  return `${BASE_URL}?${queryParams.toString()}`;
};

export const getScriptQueryString = (auth?: string, environment?: string) => {
  let queryString = '';

  if (auth) queryString += `&gtm_auth=${auth}`;
  if (environment) queryString += `&gtm_preview=env-${environment}&gtm_cookies_win=x`;

  return queryString;
};

export const pageview = (url: string) => {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  window.dataLayer.push({
    event: 'pageview',
    page: url,
  });
};

/**
 * Events and their extra properties that can be pushed to the dataLayer
 */
export type Events = {
  event: 'filter_gebruik';
  filter_categories: string;
  filter_items: string;
  filter_locatie?: 'Aanbod Overzichtspagina' | 'Woongebouw overzichtspagina' | 'Aanbod Overzicht (Kaart)';
} | {
  event: 'select_aanbod',
  item_locatie?: 'Homepage' | 'Aanbod Overzichtspagina' | 'Woongebouw overzichtspagina' | 'Aanbod Overzichtspagina (Kaart)' | 'Woongebouw Detailpagina' | 'Header Zoekfunctie'; // Op welk soort pagina wordt de advertentie geselecteerd (bijv. Home, overzicht, etc.)
  property_naam?: string; // advert.Name - Naam van aanbod
  woongebouw_naam?: string; // Naam Woongebouw waarin aanbod zich bevindt
  doelgroep?: string | string[], // Als het mogelijk is fijn om mee te nemen, dit heeft als doeleinde gebruikerssegmenten aanmaken in analytics
  aanbod_type?: string; // e.g. sociale huur, vrije sector huur, koop, etc.
  property_type?: string; // Appartement, studio, wat voor soort woningen zoeken bezoekers vooral?
  property_region?: string; // Welke regio's zijn populair in de zoektocht naar een woning
  property_district?: string; // Welke districten zijn populair in de zoektocht naar een woning
  property_price?: string | number; // Om vragen te beantwoorden zoals 'tot in welke mate beinvloed prijs van woning het aantal selects van een advertentie'
  property_price_min?: string | number; // Om vragen te beantwoorden zoals 'tot in welke mate beinvloed prijs van woning het aantal selects van een advertentie'
  property_price_max?: string | number; // Om vragen te beantwoorden zoals 'tot in welke mate beinvloed prijs van woning het aantal selects van een advertentie'
  property_surface?: string | number; // Woning eigenschap, worden voornamelijk grote woningen geselecteerd, kleine, maakt het uberhaupt uit?
  property_surface_min?: string | number; // Woning eigenschap, worden voornamelijk grote woningen geselecteerd, kleine, maakt het uberhaupt uit?
  property_surface_max?: string | number; // Woning eigenschap, worden voornamelijk grote woningen geselecteerd, kleine, maakt het uberhaupt uit?
  property_rooms?: string | number; // Zelfde als Surface
  property_rooms_min?: string | number; // Zelfde als Surface
  property_rooms_max?: string | number; // Zelfde als Surface
  property_energyLabel?: string; // Zelfde als Surface
  sw_campaign?: string; // Interessant voor MarCom of de campagne tags ervoor zorgen voor een hogere click through rate naar de advertenties
  ws_campaign?: string; // Interessant voor MarCom of de campagne tags ervoor zorgen voor een hogere click through rate naar de advertenties
  huurhulp_active: true | false // true als bezoeker ondersteunt wordt door huurhulp, false wanneer niet het geval
} | {
  event: 'select_woongebouw';
  item_locatie?: 'Aanbod Detailpagina' | 'Woongebouw overzichtspagina';
  woongebouw_naam?: string;
  doelgroep?: string | string[];
  woongebouw_regio?: string;
  woongebouw_district?: string;
  available?: number;
} | {
  event: 'select_project';
  project_type: 'project' | 'nieuwbouw';
  item_locatie: 'Project overzichtspagina' | 'Gerelateerde projecten';
  project_naam: string; // Naam van het project of nieuwbouw
} | {
  event: 'view_project';
  project_type: 'project' | 'nieuwbouw';
  project_naam: string;
} | {
  event: 'wis_filters',
  filter_locatie?: 'Aanbod Overzichtspagina' | 'Woongebouw overzichtspagina' | 'Aanbod Overzicht (Kaart)',
} | {
  event: 'start_reactie';
  property_naam?: string;
  aanbod_type?: string; // e.g. sociale huur, vrije sector huur, koop, etc.
  woongebouw_naam?: string;
  doelgroep?: string | string[];
  property_type?: string;
  property_region?: string;
  property_district?: string;
  property_price?: number;
  property_surface?: number;
  property_rooms?: number;
  property_energyLabel?: string;
  sw_campaign?: string;
  ws_campaign?: string;
} | {
  event: 'reactie_voltooid';
  property_naam?: string;
  aanbod_type?: string; // e.g. sociale huur, vrije sector huur, koop, etc.
  woongebouw_naam?: string;
  doelgroep?: string | string[];
  property_type?: string;
  property_region?: string;
  property_district?: string;
  property_price?: number;
  property_surface?: number;
  property_rooms?: number;
  property_energyLabel?: string;
  sw_campaign?: string;
  ws_campaign?: string;
} | {
  event: 'view_aanbod',
  property_naam?: string;
  aanbod_type?: string; // e.g. sociale huur, vrije sector huur, koop, etc.
  woongebouw_naam?: string;
  doelgroep?: string | string[];
  property_type?: string;
  property_region?: string;
  property_district?: string;
  property_price?: number;
  property_surface?: number;
  property_rooms?: number;
  property_energyLabel?: string;
  sw_campaign?: string;
  ws_campaign?: string;
} | {
  event: 'view_woongebouw',
  item_locatie?: 'Woongebouw Detailpagina' | 'Woongebouw overzichtspagina';
  woongebouw_regio?: string;
  woongebouw_district?: string;
  woongebouw_naam?: string;
  available?: number;
  doelgroep?: string | string[];
} | {
  event: 'search_result_click';
  search_term: string; // Interessant om te zien hoeveel van een woord af is voordat er al een resultaat wordt gevonden door bezoeker
  result: string; // De titel van het resultaat
  search_location: 'Header'; // Afhankelijk van waar de search plaatsvindt (bijv. Header) (later evt. FAQ, Reparatie Flow)
} | {
  event: 'view_search_results'; // Als de query resultaten teruggeeft
  search_term: string; // Welke query ingevoerd is
  search_location: 'Header'; // Afhankelijk van waar de search plaatsvindt (bijv. Header) (later evt. FAQ, Reparatie Flow)
  has_results: boolean; // Of de query resultaten heeft
} | {
  event: 'select_content';
  item_locatie: 'Homepage' | 'Nieuws overzichtspagina' | 'Persberichten overzichtspagina' | 'Nieuws detailpagina' | 'Project detailpagina'; // Pagina waarop content geselecteerd wordt)
  content_titel?: string; // Titel van content
  content_type: string; // nieuws_item, blog, etc.
} | {
  event: 'view_content',
  content_titel: string, // Titel van content
  content_type: string, //nieuws_item, blog, etc.
} | {
  event: 'open_marker',
  property_naam: string, //Naam van aanbod
  woongebouw_naam: string, //Naam Woongebouw waarin aanbod zich bevindt
  property_region: string, //Welke regio's zijn populair in de zoektocht naar een woning
  property_district: string, //Welke districten zijn populair in de zoektocht naar een woning
} | {
  event: 'file_download',
  file_name?: string, // Documentnaam
  extension?: string, // bestandstype (e.g. pdf, etc.)
} | {
  event: 'open_huurhulp' | 'close_huurhulp';
  property_type: string; // Welk aanbod type biedt de huurhulp aan
} | {
  event: 'huurhulp_submitted';
  household_persons: string; // Hoeveel personen heeft het huishouden
  household_income: string; // Ingediende staffel van inkomen
  property_type: string; // Welk aanbod type biedt de huurhulp aan
} | {
  event: 'expandable_link',
  interaction_type: 'open' | 'close',
  link_titel: string, // Titel expandable links
  link_subtitel_type: string, //subtitel bijv. vraag
} | {
  event: 'service_contact';
  content_category: string; // Type hoofd categorie van de pagina waarop contact wordt gezocht
  contact_type: string; // Afhankelijk van wat er aangeklikt wordt / beschikbaar is in het blok
} | {
  event: 'brochure_download';
  property_naam: string;
  aanbod_type: string;
} | {
  event: 'newsletter_signup';
  nieuwsbrief_naam: string;
  pagina_naam: string;
  pagina_type: string;
  project_naam?: string;
  project_type?: 'project' | 'nieuwbouw';
};

export const trackEvent = (event: Events) => {
  if (process.env.NODE_ENV !== 'production') {
    console.groupCollapsed(`Track event: ${event.event}`);
    Object.entries(event).forEach(([key, value]) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();
  }

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  window.dataLayer.push({
    ...event,
  });
};

export const trackReactieVoltooid = (advert: Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_AdvertisementDetailV3 | Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_NieuwbouwTypeDetailV3 | null) => {
  if (advert?.variant === 'normaal') {
    const advertNormaal = advert as Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_AdvertisementDetailV3;

    trackEvent({
      event: 'reactie_voltooid',
      aanbod_type: advertNormaal.typeLabel ?? undefined,
      property_naam: advertNormaal.name ?? undefined,
      property_type: advertNormaal.objectDetails?.objectTypeName ?? undefined,
      woongebouw_naam: advertNormaal.objectDetails?.complex?.complexInfo?.name ?? undefined,
      property_region: advertNormaal?.objectDetails?.address?.region ?? undefined,
      property_district: advertNormaal?.objectDetails?.address?.district ?? undefined,
      property_surface: advertNormaal.objectDetails?.propertySpecification?.surface ?? undefined,
      property_rooms: advertNormaal?.objectDetails?.roomSpecification?.rooms ?? undefined,
      property_price: advertNormaal.buyInfo?.price ?? advertNormaal.rentInfo?.price ?? undefined,
      property_energyLabel: advertNormaal.energyLabel?.label ?? undefined,
      ws_campaign: advertNormaal.campaign?.name ?? undefined,
    });
  }

  if (advert?.variant === 'nieuwbouw') {
    const advertNieuwbouw = advert as Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_NieuwbouwTypeDetailV3;

    trackEvent({
      event: 'reactie_voltooid',
      aanbod_type: advertNieuwbouw.typeLabel ?? undefined,
      property_naam: advertNieuwbouw.name ?? undefined,
      property_type: advertNieuwbouw.unitType ?? undefined,
      property_region: advertNieuwbouw.projectAddress?.region ?? undefined,
      property_district: advertNieuwbouw.projectAddress?.district ?? undefined,
      property_price: advertNieuwbouw.price?.max ?? undefined,
      property_surface: advertNieuwbouw.surfaceArea?.max ?? undefined,
      property_rooms: advertNieuwbouw.rooms?.max ?? undefined,
      property_energyLabel: advertNieuwbouw.energyLabel?.label ?? undefined,
      ws_campaign: advertNieuwbouw.campaign?.name ?? undefined,
    });
  }
};
